import "../../utils/string.extension";
import { toast } from "react-toastify";
import { isEmpty } from "../../utils/Util";
import { useEffect } from "react";
import {
  EverestApi,
  TargetPayApi,
  GetCompanyDealerId
} from "../../utils/UtilTarget";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  useTheme
} from "@mui/material";
import React from "react";
import { useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import { Helmet } from "react-helmet-async";
import { datadogLogs } from "@datadog/browser-logs";

export default function Terminals(props: any) {
  const [Terminals, SetTerminals] = React.useState<any>(null);
  const [counter, setCounter] = React.useState<any>(0);
  const [saving, setSaving] = React.useState(false);
  const dealerId = useAppSelector((state: RootState) => state.dealer.dealerId);
  const theme = useTheme();
  const [dealerLocations, setDealerLocations] = React.useState<any>([]);

  const dataLogger = datadogLogs.logger;

  useEffect(() => {
    GetStoreTerminals();
    GetDealerLocations();
  }, []);

  async function GetStoreTerminals() {
    var res = await TargetPayApi(
      "StoreTerminals",
      { dealerId: dealerId },
      "get",
      "PerseusPay",
      true
    );

    SetTerminals("");

    if (isEmpty(res)) return;

    const status = res.response?.status;
    const isClientError = status >= 400 && status < 500;
    const isServerError = status >= 500 && status < 600;

    if (isClientError || isServerError) {
      const message = isServerError
        ? "Unable to load terminals due to a server error. Please try again later or contact support if the issue persists."
        : "Unable to load terminals. Please refresh the page or contact support if the issue persists.";

      toast.error(message, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
        toastId: "store-terminals-error"
      });

      dataLogger.error(
        `StoreTerminals ${
          isServerError ? "5xx" : "4xx"
        } error for DealerId: ${dealerId}. Status: ${status} - ${
          res.response?.statusText
        }`
      );

      return;
    }

    if (!isEmpty(res.data)) SetTerminals(res);
  }

  function OnTerminalChange(row: any, value: any) {
    if (row.OriginalValue === undefined)
      row.OriginalValue = row["terminalName"];
    row["terminalName"] = value;
    row.hasChanges = true;
    setCounter(counter + 1);
  }

  function OnLocationChange(row: any, value: any) {
    if (row.OriginalLocation === undefined)
      row.OriginalLocation = row["dealerLocationId"] ?? 0;
    row["dealerLocationId"] = value;
    row.hasChanges = true;
    setCounter(counter + 1);
  }

  function hasRowChanges(row: any): any {
    return (
      (row.OriginalValue != undefined &&
        row.OriginalValue != row.terminalName) ||
      (row.OriginalLocation != undefined &&
        row.OriginalLocation != row.dealerLocationId)
    );
  }

  function hasChanges(): any {
    if (isEmpty(Terminals) || isEmpty(Terminals.data)) return false;
    const result = Terminals.data.filter(
      d =>
        (d.OriginalValue != undefined && d.terminalName != d.OriginalValue) ||
        (d.OriginalLocation != undefined &&
          d.dealerLocationId != d.OriginalLocation)
    );
    return result.length > 0;
  }

  async function SaveChanges() {
    const result = Terminals.data.filter((d) => d.hasChanges == true);
    const terminals = result.map(function (item) {
    return {
        id: item.id,
        terminalName: item.terminalName,
        dealerLocationId: item.dealerLocationId
      };
    });

    setSaving(true);
    try {
      const res = await TargetPayApi(
        "SaveTerminals",
        JSON.stringify({ dealerId: dealerId, terminals: terminals }),
        "post"
      );

      if (!isEmpty(res)) {
        const parsed = typeof res === "string" ? JSON.parse(res) : res;
        toast.success(parsed.Response);
        GetStoreTerminals();
      }
    } finally {
      setSaving(false);
    }
  }

  async function GetDealerLocations(): Promise<any> {
    try {
      let obj: any = new Object();
      let objBody: any = new Object();

      obj.query = "PerseusPay.GetDealerLocations";
      objBody.DealerId = dealerId;

      obj.queryBody = JSON.stringify(objBody);

      const response = await TargetPayApi(
        "query",
        JSON.stringify(obj),
        "post",
        "Everest"
      );

      if (response) {
        setDealerLocations(response);
      }
    } catch (error) {
      alert(error);
    }
  }

  return (
    <>
      <Helmet>
        <title> Terminal Setup | {theme.browserTitle} </title>
      </Helmet>
      <Box display="flex" justifyContent="center" alignItems="center">
        {Terminals == null && (
          <Box>
            Please wait. Processing.....<CircularProgress></CircularProgress>{" "}
          </Box>
        )}
        {isEmpty(Terminals) && Terminals != null && (
          <Box style={{ padding: "10px 20px" }}>
            <h4>No Terminal found.</h4>
          </Box>
        )}
        {!isEmpty(Terminals) && (
          <Paper style={{ padding: "0px" }}>
            <Box
              style={{
                padding: "5px",
                fontSize: "20px",
                backgroundColor: "#EBEDEF"
              }}
            >
              Terminal Setup
            </Box>
            <Table size={"small"} className="sticky-header">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Terminal Id</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Terminal Alias
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Last Activity
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Serial#</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!isEmpty(Terminals) &&
                  Terminals.data?.map((row: any, index: any) => {
                    return (
                      <TableRow key={index}>
                        <TableCell>
                          {row.id}
                          <span style={{ color: "red" }}>
                            {hasRowChanges(row) ? " ***" : ""}
                          </span>{" "}
                        </TableCell>
                        <TableCell>
                          <TextField
                            style={{ width: "250px" }}
                            required
                            id="outlined-required"
                            size="small"
                            value={row.terminalName}
                            onChange={e =>
                              OnTerminalChange(row, e.target.value)
                            }
                            inputProps={{ style: { fontSize: "small" } }}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            id="location-select"
                            sx={{
                              fontSize: "normal",
                              width: "330px",
                              marginRight: "10px",
                              "& .MuiSelect-select": {
                                paddingTop: "8px",
                                paddingBottom: "6px",
                                paddingLeft: "14px",
                                paddingRight: "32px"
                              }
                            }}
                            value={row.dealerLocationId}
                            onChange={e =>
                              OnLocationChange(row, e.target.value)
                            }
                          >
                            <MenuItem sx={{ fontSize: "small" }} value={0}>
                              All
                            </MenuItem>
                            {dealerLocations.map((location: any, index) => {
                              return (
                                <MenuItem
                                  key={index}
                                  sx={{ fontSize: "small" }}
                                  value={location.DealerId}
                                >
                                  {location.Name}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </TableCell>
                        <TableCell>
                          {isEmpty(row.lastActivityAt)
                            ? ""
                            : row.lastActivityAt.toUSDateFormat()}
                        </TableCell>
                        <TableCell>{row.serialNumber}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            <Box style={{ textAlign: "right", padding: "0px 10px 10px 0px" }}>
              
              <Button
                disabled={!hasChanges() || saving}
                style={{ marginTop: "20px", textAlign: "right" }}
                variant="contained"
                onClick={e => SaveChanges()}
              >
                {saving ? <><CircularProgress size={20} color="inherit" />&nbsp;Processing...</> : "Save Changes"}
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    </>
  );
}
